import { MixedSchema, isMixedSchema } from '@principle-theorem/editor';
import {
  TypeGuard,
  isDocRef,
  isEnumValue,
  isNamedTypeDocument,
  isObject,
  isTimestamp,
  type DocumentReference,
  type INamedTypeDocument,
  type ISoftDelete,
  type Timestamp,
} from '@principle-theorem/shared';
import { isBoolean, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { IAppointment } from '../appointment/appointment';
import { type IBrand } from '../brand';
import { type ITreatmentConfiguration } from '../clinical-charting/treatment/treatment-configuration';
import { ITreatmentPlan } from '../clinical-charting/treatment/treatment-plan';
import { ITreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { type ICloudTask } from '../cloud-task/cloud-task';
import { ConditionLogicConfigurationCollection } from '../condition-logic';
import { IInvoice } from '../invoice/invoice';
import type { IMarketingCampaignEntry } from '../marketing/campaign-entry';
import { IPatient } from '../patient/patient';
import { type IPractice } from '../practice/practice';
import { TemplateScope } from '../templating/template-scope';
import { type IAutomationTiming } from './automation-timing';

export enum AutomationStatus {
  Pending = 'pending',
  Queued = 'queued',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
  Disabled = 'disabled',
  Skipped = 'skipped',
}

export enum AutomationParentScope {
  TreatmentStep = 'treatmentStep',
  Patient = 'patient',
}

export enum AutomationType {
  GeneratedTask = 'generatedTask',
  AutomatedNotification = 'automatedNotification',
  AutomatedFormIssue = 'automatedFormIssue',
  CloudTaskReschedule = 'cloudTaskReschedule',
}

export const AUTOMATION_TYPES_LABEL_MAP: { [key in AutomationType]: string } = {
  [AutomationType.GeneratedTask]: 'Task',
  [AutomationType.AutomatedNotification]: 'Notification',
  [AutomationType.AutomatedFormIssue]: 'Form',
  [AutomationType.CloudTaskReschedule]: '',
};

export function isAutomationType(data: unknown): data is AutomationType {
  return data && isEnumValue(AutomationType, data) ? true : false;
}

export enum AutomationCreator {
  Staffer = 'staffer',
  TreatmentConfiguration = 'treatmentConfiguration',
  Brand = 'brand',
  Practice = 'practice',
  MarketingCampaign = 'marketingCampaign',
}

export enum AutomationTemplateKey {
  DepositReminderEmail = 'depositReminderEmail',
}

export const AUTOMATION_CREATOR_LABEL_MAP: {
  [key in AutomationCreator]: string;
} = {
  [AutomationCreator.Brand]: 'Created by Appointment Automation',
  [AutomationCreator.Practice]: 'Created by Appointment Automation',
  [AutomationCreator.Staffer]: 'Created by Staffer',
  [AutomationCreator.TreatmentConfiguration]:
    'Created by Treatment-specific Automation',
  [AutomationCreator.MarketingCampaign]: 'Created by Marketing Campaign',
};

export const AUTOMATION_CREATOR_ICON_MAP: {
  [key in AutomationCreator]: string;
} = {
  [AutomationCreator.Brand]: 'smart_toy',
  [AutomationCreator.Practice]: 'smart_toy',
  [AutomationCreator.Staffer]: 'person',
  [AutomationCreator.TreatmentConfiguration]: 'local_hospital',
  [AutomationCreator.MarketingCampaign]: 'campaign',
};

export interface IRenderedTemplate {
  text: string;
  html: string;
}

export const isRenderedTemplate = TypeGuard.interface<IRenderedTemplate>({
  text: isString,
  html: isString,
});

export const EMPTY_RENDERED_TEMPLATE: IRenderedTemplate = {
  text: '',
  html: '',
};

export interface IAutomationCustomTemplate {
  content: MixedSchema;
  scope?: TemplateScope;
  renderedTemplate?: IRenderedTemplate;
}

export const isAutomationCustomTemplate =
  TypeGuard.interface<IAutomationCustomTemplate>({
    content: isMixedSchema,
    scope: TypeGuard.undefinedOr(TypeGuard.enumValue(TemplateScope)),
    renderedTemplate: TypeGuard.undefinedOr(isRenderedTemplate),
  });

export type IAutomationScopeData =
  | {
      scope: TemplateScope.None;
    }
  | {
      scope: TemplateScope.Patient;
      patientRef: DocumentReference<IPatient>;
    }
  | {
      scope: TemplateScope.Appointment;
      appointmentRef: DocumentReference<IAppointment>;
    }
  | {
      scope: TemplateScope.TreatmentPlan;
      treatmentPlanRef: DocumentReference<ITreatmentPlan>;
    }
  | {
      scope: TemplateScope.TreatmentStep;
      treatmentStepRef: DocumentReference<ITreatmentStep>;
    }
  | {
      scope: TemplateScope.Invoice;
      invoiceRef: DocumentReference<IInvoice>;
    };

export type IAutomationScopeRequirements =
  | {
      parentScope: AutomationParentScope.TreatmentStep;
    }
  | ({
      parentScope: AutomationParentScope.Patient;
    } & IAutomationScopeData);

export interface IAutomationConfiguration extends ISoftDelete {
  isActive: boolean;
  practiceRef?: DocumentReference<IPractice>;
  treatmentRefs: DocumentReference<ITreatmentConfiguration>[];
}

export const isAutomationConfiguration =
  TypeGuard.interface<IAutomationConfiguration>({
    deleted: isBoolean,
    isActive: isBoolean,
    treatmentRefs: TypeGuard.arrayOf(isDocRef),
    practiceRef: TypeGuard.undefinedOr(isDocRef),
  });

export interface IAutomationResource {
  timing: IAutomationTiming;
  requiredConditions?: ConditionLogicConfigurationCollection;
}

export type IAutomation<Resource extends IAutomationResource> = {
  type: AutomationType;
  status: AutomationStatus;
  data: Resource;
  cloudTask?: ICloudTask;
  creator: INamedTypeDocument<object, AutomationCreator>;
  triggerDate?: Timestamp;
  triggerAfterDate?: Timestamp;
  cloudTaskHistory: ICloudTask[];
  brandRef: DocumentReference<IBrand>;
  practiceRef: DocumentReference<IPractice>;
  configRef?: DocumentReference<IAutomationConfiguration>;
  campaignEntryRef?: DocumentReference<IMarketingCampaignEntry>;
  errorMessage?: string;
} & (
  | {
      parentScope?: AutomationParentScope.TreatmentStep;
    }
  | {
      parentScope: AutomationParentScope.Patient;
      scopeData: IAutomationScopeData;
    }
);

export function isAutomation<T extends IAutomationResource>(
  automation: unknown,
  dataGuard: (data: unknown) => data is T
): automation is IAutomation<T> {
  return (
    isObject(automation) &&
    isEnumValue(AutomationType, automation.type) &&
    isEnumValue(AutomationStatus, automation.status) &&
    TypeGuard.undefinedOr(isTimestamp)(automation.triggerDate) &&
    isNamedTypeDocument(automation.creator) &&
    dataGuard(automation.data)
  );
}

export const AUTOMATION_STATUS_COLOUR_MAP: {
  [key in AutomationStatus]: string;
} = {
  [AutomationStatus.Pending]: 'default',
  [AutomationStatus.Queued]: 'accent',
  [AutomationStatus.Completed]: 'primary',
  [AutomationStatus.Skipped]: 'primary',
  [AutomationStatus.Failed]: 'warn',
  [AutomationStatus.Cancelled]: 'warn',
  [AutomationStatus.Disabled]: 'warn',
};

export const AUTOMATION_GRACE_PERIOD = moment.duration(2, 'minutes');
